import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";

const ContratarSection = () => {
  const [showForm, setShowForm] = useState(false);
  const firstInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    email: "",
    objetivo: "",
    empresa_trampa: "" // honeypot anti-spam (debe quedar vacío)
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.empresa_trampa) return; // si el bot llena esto, no enviamos
    console.log("Enviando datos: ", formData);
    alert("Información enviada correctamente.");
    setShowForm(false);
  };

  const closeModal = () => setShowForm(false);

  // Evitar scroll de fondo + enfocar primer campo
  useEffect(() => {
    document.body.style.overflow = showForm ? "hidden" : "auto";
    if (showForm && firstInputRef.current) firstInputRef.current.focus();
  }, [showForm]);

  // --- Datos de ofertas (para UI y JSON-LD) ---
  const offers = [
    {
      name: "ALMUERZO/CENA",
      tagline: "Toma prestado el cerebro de Ernesto",
      price: 1000,
      currency: "USD",
      bg: "https://img.freepik.com/fotos-premium/grupo-amigos-diversos-reunidos-recepcion-boda_53876-50474.jpg",
    },
    {
      name: "CONFERENCIA",
      tagline: "Comienza a partir de",
      price: 3500,
      currency: "USD",
      bg: "https://img.freepik.com/foto-gratis/equipo-negocios-discutiendo-sus-ideas-mientras-trabajaba-oficina_171337-5952.jpg?uid=R173083927&ga=GA1.1.1759052710.1733761302&semt=ais_incoming",
    },
    {
      name: "CONSULTORÍA ESPECIALIZADA",
      tagline: "Comienza a partir de",
      price: 5000,
      currency: "USD",
      bg: "https://img.freepik.com/foto-gratis/empleado-empresa-que-presenta-investigacion-analitica-reunion-negocios-mostrando-estadisticas-tablero-digital-presentacion-informe-ingresos-marketing-estrategia-ventas-planificacion-equipos-diversos-disparo-tripode_482257-60511.jpg?uid=R173083927&ga=GA1.1.1759052710.1733761302&semt=ais_incoming",
    },
  ];

  // --- Schema.org: Persona que ofrece Servicios con Ofertas ---
  const offersSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Ernesto Mora",
    "jobTitle": "Speaker y Consultor en IA aplicada a la SST",
    "url": "https://ernestomora.com/",
    "makesOffer": offers.map((o) => ({
      "@type": "Offer",
      "price": o.price,
      "priceCurrency": o.currency,
      "availability": "https://schema.org/InStock",
      "itemOffered": {
        "@type": "Service",
        "name": o.name,
        "description": `${o.name} — SST e Inteligencia Artificial aplicada para líderes HSE.`,
        "areaServed": "Latinoamérica"
      }
    }))
  };

  return (
    <section
      id="contratar"
      className="relative text-white py-16"
      aria-label="Contratar a Ernesto Mora: conferencias y consultoría en SST e IA"
      style={{
        backgroundImage:
          "url('https://celebratufiesta.com.co/wp-content/uploads/2025/01/ErnestoFIRMANDO-scaled.jpg')",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "left",
        width: "100%",
      }}
    >
      <Helmet>
        <title>Contratar a Ernesto Mora | Conferencista y Consultor en SST e IA</title>
        <meta
          name="description"
          content="Contrata a Ernesto Mora para conferencias, consultoría especializada y sesiones 1:1 en Seguridad y Salud en el Trabajo con IA aplicada. Precios y opciones."
        />
        <script type="application/ld+json">{JSON.stringify(offersSchema)}</script>
      </Helmet>

      {/* Superposición para legibilidad */}
      <div className="absolute inset-0 bg-yellow-600 bg-opacity-80" aria-hidden="true"></div>

      <div className="relative z-10 container mx-auto px-6 text-center">
        {/* Firma / imagen descriptiva */}
        <div className="mb-8">
          <img
            src="https://celebratufiesta.com.co/wp-content/uploads/2025/01/Firmaabajo-1.png"
            alt="Ernesto Mora — Firma"
            className="mx-auto max-w-full h-auto"
            loading="lazy"
            width="480"
            height="120"
          />
        </div>

        {/* H2 con keywords */}
        <h2 className="text-3xl md:text-4xl font-extrabold mb-3">
          Contrata conferencias y consultoría en SST e IA aplicada
        </h2>
        <p className="max-w-3xl mx-auto text-gray-100 mb-10">
          Conferencista, asesor y formador para líderes HSE en Latinoamérica. Programas
          de <strong>Seguridad y Salud en el Trabajo</strong> potenciados con{" "}
          <strong>inteligencia artificial</strong>.
        </p>

        {/* Ofertas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {offers.map((o) => (
            <article
              key={o.name}
              className="relative flex items-center justify-center p-6 rounded-lg shadow-lg bg-black bg-opacity-80 min-h-[300px]"
              role="group"
              aria-label={`${o.name} — ${o.tagline}`}
              style={{
                backgroundImage: `url('${o.bg}')`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-60 filter grayscale" aria-hidden="true"></div>
              <div className="relative z-10 text-center">
                <h3 className="text-2xl font-bold mb-2">{o.name}</h3>
                <p className="text-gray-300 mb-4">{o.tagline}</p>
                <span className="text-3xl font-bold">
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: o.currency,
                    maximumFractionDigits: 0
                  }).format(o.price)}{" "}
                </span>
              </div>
            </article>
          ))}
        </div>

        <button
          className="mt-10 px-8 py-4 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition"
          onClick={() => setShowForm(true)}
          aria-haspopup="dialog"
          aria-controls="contacto-modal"
        >
          ¡ CONTACTAR A ERNESTO !
        </button>
      </div>

      {/* Modal de contacto */}
      {showForm && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
          onClick={closeModal}
        >
          <div
            id="contacto-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contacto-title"
            className="bg-white text-black rounded-lg w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#FFD700] via-[#FFC700] to-[#FFB800] px-6 py-4 rounded-t-lg">
              <h3 id="contacto-title" className="text-xl font-bold">Formulario de Contacto</h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* honeypot (oculto) */}
              <input
                type="text"
                name="empresa_trampa"
                autoComplete="off"
                tabIndex="-1"
                className="hidden"
                aria-hidden="true"
              />

              <div className="mb-4">
                <label htmlFor="nombre" className="block text-gray-700">Nombre*</label>
                <input
                  id="nombre"
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  autoComplete="name"
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="telefono" className="block text-gray-700">Teléfono*</label>
                <input
                  id="telefono"
                  type="tel"
                  inputMode="tel"
                  pattern="[0-9+()\\-\\s]{7,}"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  autoComplete="tel"
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  ref={firstInputRef}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="block text-gray-700">E-mail*</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  required
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="objetivo" className="block text-gray-700">
                  ¿Con qué objetivo quieres contratar a Ernesto?*
                </label>
                <textarea
                  id="objetivo"
                  name="objetivo"
                  value={formData.objetivo}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded"
                >
                  Enviar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ContratarSection;
